import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { HeroBanner } from "@/components/HeroBanner";
import { useHeroSettings } from "@/hooks/use-hero-settings";
import { submitContactMessage } from "@/lib/contact.functions";
import { useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Phone, Mail, MapPin, Send } from "lucide-react";
import { z } from "zod";
import { CONTACT } from "@/config/contact";
import { buildBreadcrumbJsonLd } from "@/lib/breadcrumb-jsonld";
import { buildSeoMeta } from "@/lib/seo";
import { buildAutoDealerJsonLd } from "@/lib/jsonld";
import { OpeningStatus } from "@/components/OpeningStatus";
import { trackConversion } from "@/lib/pixel";
import { getGaClientId } from "@/lib/ga-client";
import { getAttributionForEvent } from "@/lib/attribution";

const BREADCRUMB_JSON_LD = buildBreadcrumbJsonLd([
  { name: "Forside", path: "" },
  { name: "Kontakt", path: "kontakt" },
]);

const contactSchema = z.object({
  name: z.string().trim().min(1, "Navn er påkrævet").max(100, "Navn må maks. være 100 tegn"),
  email: z.string().trim().min(1, "Email er påkrævet").email("Ugyldig email-adresse").max(255, "Email må maks. være 255 tegn"),
  phone: z.string().trim().max(20, "Telefonnummer er for langt").optional().or(z.literal("")),
  message: z.string().trim().min(1, "Besked er påkrævet").min(10, "Beskeden skal være mindst 10 tegn").max(2000, "Beskeden må maks. være 2000 tegn"),
});

type FieldErrors = Partial<Record<keyof z.infer<typeof contactSchema>, string>>;

export const Route = createFileRoute("/kontakt")({
  head: () => {
    const seo = buildSeoMeta({
      title: "Kontakt AutoXChange · Bilforhandler i Børkop",
      description:
        "Kontakt AutoXChange — bilforhandler i Trekantområdet. 10 min fra Vejle, 25 min fra Kolding. Industrivej 127, 7080 Børkop. Ring, skriv eller book prøvetur.",
      path: "/kontakt",
      imageAlt: "Kontakt AutoXChange — bilforhandler i Trekantområdet, Industrivej 127, Børkop",
      geo: {
        region: "DK-83",
        placename: "Børkop",
        latitude: 55.6379,
        longitude: 9.6431,
      },
    });
    return {
      ...seo,
      scripts: [
        { type: "application/ld+json", children: buildAutoDealerJsonLd() },
      ],
    };
  },
  component: Kontakt,
});

function Kontakt() {
  const submit = useServerFn(submitContactMessage);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const { data: hero } = useHeroSettings("contact");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = contactSchema.safeParse(form);
    if (!result.success) {
      const fieldErrors: FieldErrors = {};
      for (const issue of result.error.issues) {
        const key = issue.path[0] as keyof FieldErrors;
        if (!fieldErrors[key]) fieldErrors[key] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }
    setErrors({});
    setSending(true);
    try {
      const visitorId =
        typeof window !== "undefined"
          ? window.localStorage.getItem("axc-visitor-id") ?? undefined
          : undefined;
      const gaClientId = getGaClientId() ?? undefined;
      const attribution = getAttributionForEvent();
      const response = await submit({
        data: {
          name: result.data.name,
          email: result.data.email,
          phone: result.data.phone || "",
          message: result.data.message,
          visitorId,
          ga_client_id: gaClientId,
          attribution,
        },
      });
      // Browser-side conversion events with the same event_id as the server
      // (Meta CAPI + GA4 MP) so both platforms can deduplicate.
      trackConversion({
        meta: {
          event: "Lead",
          params: { content_name: "Kontaktformular", content_category: "contact" },
          eventId: response?.eventId,
        },
        ga: {
          event: "generate_lead",
          params: { event_id: response?.eventId, currency: "DKK", source: "contact_form" },
        },
      });
      setSent(true);
      setForm({ name: "", email: "", phone: "", message: "" });
      toast.success("Tak — vi har modtaget din besked!", { description: "Vi har sendt en bekræftelse til din mail." });
    } catch {
      toast.error("Det lykkedes desværre ikke", { description: `Prøv igen, eller ring til os direkte på ${CONTACT.phoneDisplay}.` });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: BREADCRUMB_JSON_LD }} />
      <HeroBanner
        title="Kontakt din bilforhandler i Trekantområdet"
        subtitle="Vi ligger i Børkop — 10 min fra Vejle, 25 min fra Kolding og tæt på Fredericia. Ring, skriv eller kig forbi pladsen."
        image={hero?.url}
        mediaType={hero?.mediaType}
      />

      <section className="bg-background py-16">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid gap-0 overflow-hidden rounded-2xl shadow-xl lg:grid-cols-2">
            {/* Info */}
            <div className="bg-muted p-10 lg:p-14">
              <p className="text-sm font-medium text-foreground/70">Ingen ventetid, ingen omstilling</p>
              <h2 className="mt-2 text-2xl font-bold text-foreground">Direkte kontakt til os</h2>

              <div className="mt-6 space-y-3">
                <div>
                  <p className="font-semibold text-foreground">{CONTACT.companyName}</p>
                  <a href={CONTACT.mapsUrl} target="_blank" rel="noopener noreferrer" className="mt-1 flex items-center gap-2 text-sm text-foreground hover:underline">
                    <MapPin size={14} className="text-accent" /> {CONTACT.addressLine}
                  </a>
                </div>
                <a href={CONTACT.emailHref} className="flex items-center gap-2 text-sm text-foreground hover:underline">
                  <Mail size={14} className="text-accent" /> {CONTACT.email}
                </a>
                <a href={CONTACT.phoneHref} className="flex items-center gap-2 text-sm text-foreground hover:underline">
                  <Phone size={14} className="text-accent" /> {CONTACT.phoneDisplay}
                </a>
                <OpeningStatus className="pl-6" />
                <p className="text-sm text-foreground">CVR: {CONTACT.cvr}</p>
              </div>

              <div className="mt-8 border-t border-border pt-8">
                <h3 className="text-lg font-semibold text-foreground">Åbningstider</h3>
                <p className="mt-2 text-sm text-foreground/70">{CONTACT.openingHoursNote}</p>
                <div className="mt-3 space-y-1.5 text-sm text-foreground/70">
                  {CONTACT.openingHours.map((slot) => (
                    <div key={slot.label} className="flex justify-between">
                      <span>{slot.label}</span>
                      <span className="font-medium text-foreground">{slot.hours}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="bg-dark-blue p-10 lg:p-14">
              <h2 className="text-xl font-semibold text-white">Skriv til os herunder</h2>
              <p className="mt-1 text-sm text-white/75">Vi vender som regel tilbage inden for et par timer.</p>

              {sent ? (
                <div className="mt-8 rounded-xl bg-white/5 p-8 text-center">
                  <p className="text-lg font-semibold text-white">Modtaget — tak!</p>
                  <p className="mt-2 text-sm text-white/80">Vi kigger på din besked og vender tilbage hurtigst muligt.</p>
                  <Button onClick={() => setSent(false)} className="mt-4 bg-accent text-accent-foreground hover:brightness-110">
                    Skriv en ny besked
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="mt-8 space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <Input
                        placeholder="Dit navn"
                        value={form.name}
                        onChange={(e) => { setForm({ ...form, name: e.target.value }); if (errors.name) setErrors((p) => ({ ...p, name: undefined })); }}
                        className={`border-white/15 bg-white/5 text-white placeholder:text-white/60 ${errors.name ? "border-red-400" : ""}`}
                      />
                      {errors.name && <p className="mt-1 text-xs text-red-400">{errors.name}</p>}
                    </div>
                    <div>
                      <Input
                        placeholder="Telefon (valgfrit)"
                        value={form.phone}
                        onChange={(e) => { setForm({ ...form, phone: e.target.value }); if (errors.phone) setErrors((p) => ({ ...p, phone: undefined })); }}
                        className={`border-white/15 bg-white/5 text-white placeholder:text-white/60 ${errors.phone ? "border-red-400" : ""}`}
                      />
                      {errors.phone && <p className="mt-1 text-xs text-red-400">{errors.phone}</p>}
                    </div>
                  </div>
                  <div>
                    <Input
                      type="email"
                      placeholder="Din email"
                      value={form.email}
                      onChange={(e) => { setForm({ ...form, email: e.target.value }); if (errors.email) setErrors((p) => ({ ...p, email: undefined })); }}
                      className={`border-white/15 bg-white/5 text-white placeholder:text-white/60 ${errors.email ? "border-red-400" : ""}`}
                    />
                    {errors.email && <p className="mt-1 text-xs text-red-400">{errors.email}</p>}
                  </div>
                  <div>
                    <Textarea
                      placeholder="Hvad kan vi hjælpe med? (mindst 10 tegn)"
                      value={form.message}
                      onChange={(e) => { setForm({ ...form, message: e.target.value }); if (errors.message) setErrors((p) => ({ ...p, message: undefined })); }}
                      rows={5}
                      maxLength={2000}
                      className={`border-white/15 bg-white/5 text-white placeholder:text-white/60 ${errors.message ? "border-red-400" : ""}`}
                    />
                    <div className="mt-1 flex justify-between">
                      {errors.message ? <p className="text-xs text-red-400">{errors.message}</p> : <span />}
                      <span className="text-xs text-white/70">{form.message.length}/2000</span>
                    </div>
                  </div>
                  <Button type="submit" disabled={sending} className="w-full bg-accent text-accent-foreground hover:brightness-110">
                    <Send size={16} className="mr-2" /> {sending ? "Sender..." : "Send besked"}
                  </Button>
                </form>
              )}
            </div>
          </div>

          <div className="mt-12 overflow-hidden rounded-2xl shadow-lg">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2252.5!2d9.65!3d55.67!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sIndustrivej%20127%2C%207080%20B%C3%B8rkop!5e0!3m2!1sda!2sdk!4v1"
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              title="AutoXChange lokation"
            />
          </div>
        </div>
      </section>
    </>
  );
}
